import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Factory, X, Upload, Check, XCircle, Merge, Star, AlertTriangle,
  ChevronLeft, ChevronRight, Link2, ArrowRight, SkipForward, PlusCircle, History, Undo2
} from 'lucide-react';
import {
  getManufacturers, createManufacturer, updateManufacturer, deactivateManufacturer,
  mergeManufacturers, getManufacturerCategories, getManufacturerUoms,
  getManufacturerSuggestions, approveSuggestion, rejectSuggestion,
  importManufacturersCsv, confirmManufacturersCsvImport, getListingScores,
  autoLinkPreview, autoLinkConfirm, getRenameLog, undoRename
} from '@/lib/api';

const COUNTRIES = [
  { code: 'DE', name: 'Deutschland' }, { code: 'US', name: 'USA' },
  { code: 'FI', name: 'Finnland' }, { code: 'IT', name: 'Italien' },
  { code: 'JP', name: 'Japan' }, { code: 'CN', name: 'China' },
  { code: 'GB', name: 'UK' }, { code: 'FR', name: 'Frankreich' },
  { code: 'NL', name: 'Niederlande' }, { code: 'SE', name: 'Schweden' },
  { code: 'AT', name: 'Osterreich' }, { code: 'CH', name: 'Schweiz' },
];

// ---- Manufacturer Form Dialog ----
const ManufacturerForm = ({ manufacturer, allCategories, allUoms, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: manufacturer?.name || '',
    aliases: manufacturer?.aliases?.join(', ') || '',
    categories: manufacturer?.categories || [],
    default_uom: manufacturer?.default_uom || '',
    country: manufacturer?.country || '',
    active: manufacturer?.active ?? true,
    internal_notes: manufacturer?.internal_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const toggleCategory = (cat) => {
    setForm(p => ({
      ...p,
      categories: p.categories.includes(cat)
        ? p.categories.filter(c => c !== cat)
        : [...p.categories, cat],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aliases: form.aliases.split(',').map(a => a.trim()).filter(Boolean),
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <Label>Hersteller-Name *</Label>
        <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          placeholder="z.B. Avery Dennison" data-testid="mfr-name-input" />
      </div>
      <div>
        <Label>Aliasse / Alternativnamen</Label>
        <Input value={form.aliases} onChange={e => setForm(p => ({ ...p, aliases: e.target.value }))}
          placeholder='z.B. AD, Avery, Avery D. (kommagetrennt)' data-testid="mfr-aliases-input" />
        <p className="text-xs text-muted-foreground mt-1">Kommagetrennte Alternativnamen fur Fuzzy-Matching</p>
      </div>
      <div>
        <Label>Material-Kategorien</Label>
        <div className="flex flex-wrap gap-2 mt-1.5" data-testid="mfr-categories-select">
          {allCategories.map(cat => (
            <Badge key={cat} variant={form.categories.includes(cat) ? 'default' : 'outline'}
              className="cursor-pointer select-none transition-colors"
              onClick={() => toggleCategory(cat)} data-testid={`mfr-cat-${cat}`}>
              {form.categories.includes(cat) && <Check className="h-3 w-3 mr-1" />}
              {cat}
            </Badge>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Standard-Einheit</Label>
          <Select value={form.default_uom} onValueChange={v => setForm(p => ({ ...p, default_uom: v }))}>
            <SelectTrigger data-testid="mfr-uom-select"><SelectValue placeholder="Wahlen..." /></SelectTrigger>
            <SelectContent>
              {allUoms.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Herkunftsland</Label>
          <Select value={form.country} onValueChange={v => setForm(p => ({ ...p, country: v }))}>
            <SelectTrigger data-testid="mfr-country-select"><SelectValue placeholder="Wahlen..." /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Interne Notizen</Label>
        <Textarea value={form.internal_notes} onChange={e => setForm(p => ({ ...p, internal_notes: e.target.value }))}
          placeholder="Nur fur Admins sichtbar..." rows={2} data-testid="mfr-notes-input" />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} data-testid="mfr-active-switch" />
        <Label>Aktiv</Label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Abbrechen</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="mfr-save-btn">
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
};

// ---- Scores SubTab ----
const ScoresSubTab = () => {
  const { t } = useTranslation();
  const [scores, setScores] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getListingScores({ page, limit: 20 });
      setScores(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error(t('admin.mfr.loadError')); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const renderStars = (score, max) => (
    <div className="flex gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star key={i} className={`h-4 w-4 ${i < score ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
      ))}
    </div>
  );

  if (loading) return <div className="py-8 text-center text-muted-foreground">Laden...</div>;

  return (
    <div data-testid="listing-scores-section">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Material</TableHead>
            <TableHead>Hersteller</TableHead>
            <TableHead>Gruppe</TableHead>
            <TableHead>SKUs</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Probleme</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scores.map(s => (
            <TableRow key={s.material_id} data-testid={`score-row-${s.material_id}`}>
              <TableCell className="font-medium max-w-[200px] truncate">{s.common_title}</TableCell>
              <TableCell>{s.manufacturer_name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{s.group_name}</TableCell>
              <TableCell>{s.sku_count}</TableCell>
              <TableCell>{renderStars(s.score, s.max_score)}</TableCell>
              <TableCell>
                {s.flags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {s.flags.map(f => (
                      <Badge key={f} variant="destructive" className="text-[10px] px-1.5 py-0">
                        <AlertTriangle className="h-3 w-3 mr-0.5" />
                        {f === 'manufacturer_unverified' ? 'Hersteller' :
                         f === 'category_missing' ? 'Kategorie' :
                         f === 'uom_missing' ? 'Einheit' : 'Menge'}
                      </Badge>
                    ))}
                  </div>
                ) : <Badge variant="outline" className="text-[10px] text-emerald-600">Vollstandig</Badge>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {total > 20 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Seite {page} / {Math.ceil(total / 20)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ---- CSV Import SubTab ----
const CsvImportSubTab = ({ onReload }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await importManufacturersCsv(file);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const res = await confirmManufacturersCsvImport(preview.items);
      toast.success(res.data.message);
      setPreview(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="csv-import-section">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="csv-upload-btn">
          <Upload className="h-4 w-4 mr-1.5" /> CSV hochladen
        </Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
        <p className="text-sm text-muted-foreground">{t('admin.mfr.csvHint')}</p>
      </div>

      {preview && (
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Import-Vorschau: {preview.total} Eintrage ({preview.matched} erkannt, {preview.unmatched} neu)
            </CardTitle>
            {preview.materials_to_rename > 0 && (
              <p className="text-sm text-emerald-600 mt-1" data-testid="csv-rename-hint">
                <ArrowRight className="inline h-3.5 w-3.5 mr-1" />
                {preview.materials_to_rename} Artikel werden auf kanonische Herstellernamen angepasst
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CSV-Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Zuordnung</TableHead>
                    <TableHead>Artikel</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{item.raw_name}</TableCell>
                      <TableCell>
                        {item.matched ? (
                          <Badge variant="outline" className="text-emerald-600"><Check className="h-3 w-3 mr-1" />Erkannt</Badge>
                        ) : (
                          <Badge variant="destructive"><Plus className="h-3 w-3 mr-1" />Neu</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.canonical_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.needs_rename && item.material_count > 0 ? (
                          <Badge variant="secondary" data-testid={`csv-rename-count-${item.raw_name}`}>
                            {item.material_count} → umbenennen
                          </Badge>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setPreview(null)}>Abbrechen</Button>
              <Button
                onClick={handleConfirm}
                disabled={importing || (preview.unmatched === 0 && preview.materials_to_rename === 0)}
                data-testid="csv-confirm-btn"
              >
                {importing
                  ? 'Importiere...'
                  : `${preview.unmatched} neue Hersteller${preview.materials_to_rename ? ` + ${preview.materials_to_rename} Artikel anpassen` : ''}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ---- Auto-Link SubTab ----
const AutoLinkSubTab = ({ onReload }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [decisions, setDecisions] = useState({});  // raw_name -> { action, canonical_name }

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await autoLinkPreview();
      setPreview(res.data);
      // Pre-fill decisions: exact non-canonical → rename, fuzzy → skip (admin decides)
      const d = {};
      (res.data.exact || []).forEach(e => {
        if (!e.already_canonical) d[e.raw_name] = { action: 'rename', canonical_name: e.canonical_name };
      });
      (res.data.fuzzy || []).forEach(f => {
        d[f.raw_name] = { action: 'skip', canonical_name: f.suggested_canonical };
      });
      (res.data.unmatched || []).forEach(u => {
        d[u.raw_name] = { action: 'skip', canonical_name: u.best_match || u.raw_name };
      });
      setDecisions(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.loadError'));
    } finally {
      setScanning(false);
    }
  };

  const setAction = (raw_name, action, canonical_name) => {
    setDecisions(p => ({ ...p, [raw_name]: { action, canonical_name: canonical_name || p[raw_name]?.canonical_name || raw_name } }));
  };

  const handleApply = async () => {
    const items = Object.entries(decisions)
      .filter(([, v]) => v.action !== 'skip')
      .map(([raw_name, v]) => ({ raw_name, canonical_name: v.canonical_name, action: v.action }));

    if (items.length === 0) { toast.info(t('admin.mfr.noSelection')); return; }

    setApplying(true);
    try {
      const res = await autoLinkConfirm(items);
      toast.success(res.data.message);
      setPreview(null);
      setDecisions({});
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
    } finally {
      setApplying(false);
    }
  };

  const actionCount = Object.values(decisions).filter(d => d.action !== 'skip').length;

  const ActionButtons = ({ rawName, item, type }) => {
    const d = decisions[rawName] || {};
    const renameTarget = type === 'fuzzy'
      ? item.suggested_canonical
      : (type === 'unmatched' ? item.best_match : item.canonical_name);
    const canRename = !!renameTarget;

    return (
      <div className="flex gap-1">
        {type !== 'exact' && (
          <Button size="sm" variant={d.action === 'rename' ? 'default' : 'outline'} className="h-7 text-xs px-2"
            disabled={!canRename}
            title={canRename ? `Umbenennen zu "${renameTarget}"` : 'Kein Ziel-Hersteller verfügbar — bitte "Neu anlegen" wählen'}
            onClick={() => {
              setAction(rawName, 'rename', renameTarget);
              toast.success(`Markiert: "${rawName}" → "${renameTarget}". Mit "Änderungen anwenden" bestätigen.`);
            }}
            data-testid={`autolink-rename-${rawName}`}>
            <ArrowRight className="h-3 w-3 mr-1" />Umbenennen
          </Button>
        )}
        {type === 'unmatched' && (
          <Button size="sm" variant={d.action === 'create_new' ? 'default' : 'outline'} className="h-7 text-xs px-2"
            onClick={() => {
              setAction(rawName, 'create_new', rawName);
              toast.success(`Neuer Hersteller: "${rawName}" wird angelegt. Mit "Änderungen anwenden" bestätigen.`);
            }}
            data-testid={`autolink-create-${rawName}`}>
            <PlusCircle className="h-3 w-3 mr-1" />Neu anlegen
          </Button>
        )}
        <Button size="sm" variant={d.action === 'skip' ? 'secondary' : 'ghost'} className="h-7 text-xs px-2"
          onClick={() => setAction(rawName, 'skip', renameTarget || rawName)}
          data-testid={`autolink-skip-${rawName}`}>
          <SkipForward className="h-3 w-3 mr-1" />Skip
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="auto-link-section">
      {!preview ? (
        <div className="text-center py-8 space-y-3">
          <Link2 className="h-10 w-10 mx-auto text-orange-500" />
          <h3 className="font-semibold text-lg">Auto-Link: Materialien verknupfen</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Scannt alle bestehenden Materialien und verknupft Freitext-Herstellernamen mit den
            kanonischen Stammdaten. Exakte Treffer werden automatisch zugeordnet, ahnliche Namen
            als Vorschlage markiert.
          </p>
          <Button onClick={handleScan} disabled={scanning} className="mt-2" data-testid="auto-link-scan-btn">
            {scanning ? (
              <span className="flex items-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Scanne...</span>
            ) : (
              <span className="flex items-center gap-2"><Link2 className="h-4 w-4" />Auto-Link starten</span>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stats Dashboard */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="auto-link-stats">
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold">{preview.total_materials}</p>
              <p className="text-xs text-muted-foreground">Materialien gesamt</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{preview.stats.verified_pct}%</p>
              <p className="text-xs text-muted-foreground">Bereits verifiziert</p>
            </Card>
            <Card className="p-3 text-center border-emerald-200 dark:border-emerald-800">
              <p className="text-2xl font-bold text-emerald-600">{preview.stats.needs_rename}</p>
              <p className="text-xs text-muted-foreground">Exakt (umzubenennen)</p>
            </Card>
            <Card className="p-3 text-center border-amber-200 dark:border-amber-800">
              <p className="text-2xl font-bold text-amber-600">{preview.stats.fuzzy_total}</p>
              <p className="text-xs text-muted-foreground">Ahnlich (Vorschlage)</p>
            </Card>
            <Card className="p-3 text-center border-red-200 dark:border-red-800">
              <p className="text-2xl font-bold text-red-600">{preview.stats.unmatched_total}</p>
              <p className="text-xs text-muted-foreground">{t('admin.mfr.noMatch')}</p>
            </Card>
          </div>

          {/* Exact matches that need renaming */}
          {preview.exact.filter(e => !e.already_canonical).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-500" />
                  Exakte Treffer — werden automatisch umbenannt ({preview.exact.filter(e => !e.already_canonical).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aktueller Name</TableHead>
                      <TableHead>Kanonischer Name</TableHead>
                      <TableHead>Materialien</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.exact.filter(e => !e.already_canonical).map(e => (
                      <TableRow key={e.raw_name}>
                        <TableCell className="font-medium">{e.raw_name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                            <ArrowRight className="h-3.5 w-3.5" />{e.canonical_name}
                          </span>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{e.material_count}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Fuzzy matches */}
          {preview.fuzzy.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Ahnliche Treffer — bitte prufen ({preview.fuzzy.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aktueller Name</TableHead>
                      <TableHead>Vorschlag</TableHead>
                      <TableHead>Ahnlichkeit</TableHead>
                      <TableHead>Materialien</TableHead>
                      <TableHead>Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.fuzzy.map(f => (
                      <TableRow key={f.raw_name}>
                        <TableCell className="font-medium">{f.raw_name}</TableCell>
                        <TableCell className="text-amber-600 font-medium">{f.suggested_canonical}</TableCell>
                        <TableCell><Badge variant="outline">{f.similarity}%</Badge></TableCell>
                        <TableCell><Badge variant="secondary">{f.material_count}</Badge></TableCell>
                        <TableCell><ActionButtons rawName={f.raw_name} item={f} type="fuzzy" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Unmatched */}
          {preview.unmatched.length > 0 && (
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Kein Match — manuell zuordnen oder als neuen Hersteller anlegen ({preview.unmatched.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Bester Match</TableHead>
                      <TableHead>Materialien</TableHead>
                      <TableHead>Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.unmatched.map(u => (
                      <TableRow key={u.raw_name}>
                        <TableCell className="font-medium">{u.raw_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {u.best_match ? `${u.best_match} (${u.best_similarity}%)` : '-'}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{u.material_count}</Badge></TableCell>
                        <TableCell><ActionButtons rawName={u.raw_name} item={u} type="unmatched" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Action bar */}
          <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-t flex items-center justify-between z-10">
            <Button variant="outline" onClick={() => { setPreview(null); setDecisions({}); }}>
              Abbrechen
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{actionCount} Änderung(en) ausgewählt</span>
              <Button onClick={handleApply} disabled={applying || actionCount === 0} data-testid="auto-link-apply-btn"
                className="shadow-md">
                {applying ? 'Wird angewendet...' : `${actionCount} Änderungen anwenden`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---- Rename Log SubTab (Undo) ----
const RenameLogSubTab = ({ onReload }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRenameLog({ limit: 100 });
      setEntries(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleUndo = async (logId, fromName, toName) => {
    if (!window.confirm(`Rename "${fromName}" → "${toName}" wirklich rückgängig machen?`)) return;
    setUndoing(logId);
    try {
      const res = await undoRename(logId);
      toast.success(res.data.message);
      await load();
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo fehlgeschlagen');
    } finally {
      setUndoing(null);
    }
  };

  const sourceLabel = (s) => s === 'csv-import' ? 'CSV-Import' : 'Auto-Link';
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="rename-log-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-orange-500" />Rename-Historie
          </h3>
          <p className="text-sm text-muted-foreground">
            Letzte Umbenennungen in den Artikel-Stammdaten. Ein versehentlicher Rename kann per Klick zurückgerollt werden.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="rename-log-reload-btn">
          {loading ? 'Lädt...' : 'Aktualisieren'}
        </Button>
      </div>

      {entries.length === 0 && !loading ? (
        <div className="text-center py-10 text-muted-foreground text-sm">
          {t('admin.mfr.noRenames')}
        </div>
      ) : (
        <Table data-testid="rename-log-table">
          <TableHeader>
            <TableRow>
              <TableHead>Datum</TableHead>
              <TableHead>Quelle</TableHead>
              <TableHead>Von</TableHead>
              <TableHead>Nach</TableHead>
              <TableHead className="text-right">Artikel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-28">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map(e => (
              <TableRow key={e.log_id} data-testid={`rename-log-row-${e.log_id}`}>
                <TableCell className="text-xs text-muted-foreground">{fmt(e.created_at)}</TableCell>
                <TableCell><Badge variant="outline">{sourceLabel(e.source)}</Badge></TableCell>
                <TableCell className="font-medium">{e.from_name}</TableCell>
                <TableCell className="text-emerald-600 font-medium flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" />{e.to_name}
                </TableCell>
                <TableCell className="text-right"><Badge variant="secondary">{e.renamed_count}</Badge></TableCell>
                <TableCell>
                  {e.reverted_at ? (
                    <span className="text-xs text-muted-foreground">Rückgängig · {fmt(e.reverted_at)}</span>
                  ) : (
                    <Badge className="bg-emerald-500 hover:bg-emerald-600">Aktiv</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {e.reverted_at ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs px-2"
                      disabled={undoing === e.log_id}
                      onClick={() => handleUndo(e.log_id, e.from_name, e.to_name)}
                      data-testid={`rename-log-undo-${e.log_id}`}>
                      <Undo2 className="h-3 w-3 mr-1" />
                      {undoing === e.log_id ? '...' : 'Undo'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};



// ---- Main ManufacturerTab ----
export const ManufacturerTab = () => {
  const { t } = useTranslation();
  const [manufacturers, setManufacturers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMfr, setEditMfr] = useState(null);
  const [mergeDialog, setMergeDialog] = useState({ open: false, source: null });
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mfrRes, catRes, uomRes, sugRes] = await Promise.all([
        getManufacturers(), getManufacturerCategories(), getManufacturerUoms(), getManufacturerSuggestions(),
      ]);
      setManufacturers(mfrRes.data);
      setCategories(catRes.data);
      setUoms(uomRes.data);
      setSuggestions(sugRes.data);
    } catch { toast.error(t('admin.mfr.loadDataError')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async (payload) => {
    try {
      if (editMfr) {
        await updateManufacturer(editMfr.manufacturer_id, payload);
        toast.success('Hersteller aktualisiert');
      } else {
        await createManufacturer(payload);
        toast.success('Hersteller erstellt');
      }
      setDialogOpen(false);
      setEditMfr(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.saveError'));
    }
  };

  const handleDeactivate = async (mfr) => {
    if (!window.confirm(`"${mfr.name}" wirklich deaktivieren?`)) return;
    try {
      await deactivateManufacturer(mfr.manufacturer_id);
      toast.success('Hersteller deaktiviert');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('admin.rating.error')); }
  };

  const handleMerge = async () => {
    if (!mergeDialog.source || !mergeTargetId) return;
    try {
      await mergeManufacturers(mergeDialog.source.manufacturer_id, mergeTargetId);
      toast.success('Hersteller zusammengefuhrt');
      setMergeDialog({ open: false, source: null });
      setMergeTargetId('');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleApproveSuggestion = async (id) => {
    try {
      await approveSuggestion(id);
      toast.success('Vorschlag genehmigt');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('admin.rating.error')); }
  };

  const handleRejectSuggestion = async (id) => {
    try {
      await rejectSuggestion(id);
      toast.success('Vorschlag abgelehnt');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('admin.rating.error')); }
  };

  const filtered = searchTerm
    ? manufacturers.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.aliases?.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : manufacturers;

  if (loading) return <div className="py-8 text-center text-muted-foreground">Lade Hersteller-Daten...</div>;

  return (
    <Card data-testid="manufacturer-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Factory className="h-5 w-5 text-orange-500" />
          Hersteller-Stammdaten
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="list" className="space-y-4">
          <TabsList className="h-auto gap-1">
            <TabsTrigger value="list" className="text-xs sm:text-sm" data-testid="mfr-list-tab">
              Hersteller ({manufacturers.length})
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="text-xs sm:text-sm" data-testid="mfr-suggestions-tab">
              Vorschlage {suggestions.length > 0 && <Badge className="ml-1 h-5 px-1.5 text-xs bg-orange-500">{suggestions.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="scores" className="text-xs sm:text-sm" data-testid="mfr-scores-tab">
              Matching-Score
            </TabsTrigger>
            <TabsTrigger value="import" className="text-xs sm:text-sm" data-testid="mfr-import-tab">
              CSV-Import
            </TabsTrigger>
            <TabsTrigger value="auto-link" className="text-xs sm:text-sm" data-testid="mfr-autolink-tab">
              <Link2 className="h-3.5 w-3.5 mr-1" />Auto-Link
            </TabsTrigger>
            <TabsTrigger value="rename-log" className="text-xs sm:text-sm" data-testid="mfr-rename-log-tab">
              <History className="h-3.5 w-3.5 mr-1" />Rename-Log
            </TabsTrigger>
          </TabsList>

          {/* ---- Manufacturer List ---- */}
          <TabsContent value="list" className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Input placeholder="Hersteller suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="max-w-xs" data-testid="mfr-search-input" />
              <Button size="sm" onClick={() => { setEditMfr(null); setDialogOpen(true); }} data-testid="add-manufacturer-btn">
                <Plus className="h-4 w-4 mr-1" /> Neuer Hersteller
              </Button>
            </div>
            <Table data-testid="manufacturers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Aliasse</TableHead>
                  <TableHead>Kategorien</TableHead>
                  <TableHead>Einheit</TableHead>
                  <TableHead>Land</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(m => (
                  <TableRow key={m.manufacturer_id} data-testid={`mfr-row-${m.manufacturer_id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(m.aliases || []).map(a => (
                          <Badge key={a} variant="secondary" className="text-[10px] px-1.5 py-0">{a}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {(m.categories || []).slice(0, 2).map(c => (
                          <Badge key={c} variant="outline" className="text-[10px] px-1.5 py-0">{c}</Badge>
                        ))}
                        {(m.categories || []).length > 2 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{m.categories.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{m.default_uom || '-'}</TableCell>
                    <TableCell className="text-sm">{m.country || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={m.active ? 'default' : 'secondary'}>
                        {m.active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setEditMfr(m); setDialogOpen(true); }}
                          data-testid={`edit-mfr-${m.manufacturer_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setMergeDialog({ open: true, source: m }); setMergeTargetId(''); }}
                          data-testid={`merge-mfr-${m.manufacturer_id}`}>
                          <Merge className="h-3.5 w-3.5" />
                        </Button>
                        {m.active && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                            onClick={() => handleDeactivate(m)}
                            data-testid={`deactivate-mfr-${m.manufacturer_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t('admin.mfr.noFound')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* ---- Suggestions ---- */}
          <TabsContent value="suggestions">
            {suggestions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">{t('admin.mfr.noOpenSuggestions')}</p>
            ) : (
              <Table data-testid="suggestions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Vorgeschlagener Name</TableHead>
                    <TableHead>Von</TableHead>
                    <TableHead>Grund</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead className="w-28">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.map(s => (
                    <TableRow key={s.suggestion_id} data-testid={`suggestion-row-${s.suggestion_id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-sm">{s.suggested_by_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{s.reason || '-'}</TableCell>
                      <TableCell className="text-sm">{new Date(s.created_at).toLocaleDateString('de-DE')}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600"
                            onClick={() => handleApproveSuggestion(s.suggestion_id)}
                            data-testid={`approve-suggestion-${s.suggestion_id}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                            onClick={() => handleRejectSuggestion(s.suggestion_id)}
                            data-testid={`reject-suggestion-${s.suggestion_id}`}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* ---- Matching Scores ---- */}
          <TabsContent value="scores">
            <ScoresSubTab />
          </TabsContent>

          {/* ---- CSV Import ---- */}
          <TabsContent value="import">
            <CsvImportSubTab onReload={loadAll} />
          </TabsContent>

          {/* ---- Auto-Link ---- */}
          <TabsContent value="auto-link">
            <AutoLinkSubTab onReload={loadAll} />
          </TabsContent>

          {/* ---- Rename Log ---- */}
          <TabsContent value="rename-log">
            <RenameLogSubTab onReload={loadAll} />
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditMfr(null); } }}>
        <DialogContent className="max-w-lg" data-testid="manufacturer-dialog">
          <DialogHeader>
            <DialogTitle>{editMfr ? 'Hersteller bearbeiten' : 'Neuer Hersteller'}</DialogTitle>
            <DialogDescription>
              {editMfr ? 'Hersteller-Stammdaten anpassen' : 'Neuen Hersteller in der Datenbank anlegen'}
            </DialogDescription>
          </DialogHeader>
          <ManufacturerForm
            manufacturer={editMfr}
            allCategories={categories}
            allUoms={uoms}
            onSave={handleSave}
            onCancel={() => { setDialogOpen(false); setEditMfr(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeDialog.open} onOpenChange={(open) => { if (!open) setMergeDialog({ open: false, source: null }); }}>
        <DialogContent className="max-w-md" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Hersteller zusammenfuhren</DialogTitle>
            <DialogDescription>
              "{mergeDialog.source?.name}" wird in den Ziel-Hersteller zusammengefuhrt.
              Alle Materialien werden automatisch umbenannt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ziel-Hersteller</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="Ziel wahlen..." /></SelectTrigger>
                <SelectContent>
                  {manufacturers
                    .filter(m => m.manufacturer_id !== mergeDialog.source?.manufacturer_id && m.active)
                    .map(m => <SelectItem key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMergeDialog({ open: false, source: null })}>Abbrechen</Button>
              <Button onClick={handleMerge} disabled={!mergeTargetId} variant="destructive" data-testid="merge-confirm-btn">
                <Merge className="h-4 w-4 mr-1" /> Zusammenfuhren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
